/**
 * Channel Detail Page
 * 
 * Chat view for a specific channel with messages, typing indicators, etc.
 */

import { useState, useEffect, useRef, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { 
  Hash, 
  Send, 
  Loader2,
  ChevronLeft,
  MoreHorizontal,
  Users,
  Settings,
  Bell,
  BellOff,
  LogOut,
  Pin,
  AtSign,
  Smile,
  Paperclip,
  Reply,
} from 'lucide-react'
import { toast } from 'sonner'
import { cn, formatMessageTime, getInitials, getAvatarColor, groupMessagesByDate } from '../lib/utils'
import { channelsApi, messagesApi, type Message, type Channel } from '../lib/api'
import { useTeamsStore } from '../store/teamsStore'

export default function ChannelDetailPage() {
  const { channelId } = useParams<{ channelId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [input, setInput] = useState('')
  const [replyTo, setReplyTo] = useState<Message | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const { drafts, setDraft, clearDraft, mutedChannels, muteChannel, unmuteChannel } = useTeamsStore()
  
  // Load draft on mount
  useEffect(() => {
    if (channelId && drafts[channelId]) {
      setInput(drafts[channelId])
    }
  }, [channelId])
  
  // Save draft on change
  useEffect(() => {
    if (channelId) {
      if (input) {
        setDraft(channelId, input)
      } else {
        clearDraft(channelId)
      }
    }
  }, [input, channelId])
  
  // Fetch channel details
  const { data: channel, isLoading: loadingChannel } = useQuery({
    queryKey: ['teams-channel', channelId],
    queryFn: () => channelsApi.get(channelId!),
    enabled: !!channelId,
  })
  
  // Fetch messages
  const { data: messagesData, isLoading: loadingMessages, refetch: refetchMessages } = useQuery({
    queryKey: ['teams-messages', channelId],
    queryFn: () => messagesApi.list(channelId!),
    enabled: !!channelId,
    refetchInterval: 5000, // Poll every 5s for new messages
  })
  
  const messages = messagesData?.messages || []
  
  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])
  
  // Focus input
  useEffect(() => {
    inputRef.current?.focus()
  }, [channelId])
  
  // Send message mutation
  const sendMutation = useMutation({
    mutationFn: (content: string) => messagesApi.send(channelId!, content, replyTo?.id),
    onSuccess: () => {
      setInput('')
      clearDraft(channelId!)
      setReplyTo(null)
      queryClient.invalidateQueries(['teams-messages', channelId])
    },
    onError: () => {
      toast.error('Failed to send message')
    },
  })
  
  const handleSend = (e?: React.FormEvent) => {
    e?.preventDefault()
    if (!input.trim() || sendMutation.isLoading) return
    sendMutation.mutate(input.trim())
  }
  
  const isMuted = channelId ? mutedChannels.includes(channelId) : false
  
  // Group messages by date
  const groupedMessages = useMemo(() => groupMessagesByDate(messages), [messages])
  
  if (loadingChannel) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
      </div>
    )
  }
  
  if (!channel) {
    return (
      <div className="h-full flex flex-col items-center justify-center">
        <Hash className="w-12 h-12 text-slate-300 mb-4" />
        <p className="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2">Channel not found</p>
        <button
          onClick={() => navigate('/channels')}
          className="text-violet-600 hover:underline"
        >
          Go back to channels
        </button>
      </div>
    )
  }
  
  return (
    <div className="h-full flex flex-col bg-white dark:bg-slate-900">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/channels')}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg lg:hidden"
          >
            <ChevronLeft className="w-5 h-5 text-slate-600 dark:text-slate-400" />
          </button>
          <div className="w-10 h-10 rounded-xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
            <Hash className="w-5 h-5 text-violet-600 dark:text-violet-400" />
          </div>
          <div>
            <h1 className="font-semibold text-slate-800 dark:text-white">{channel.name}</h1>
            <p className="text-xs text-slate-500">{channel.members?.length || channel.member_count} members</p>
          </div>
        </div>
        
        <div className="flex items-center gap-1">
          <button 
            onClick={() => isMuted ? unmuteChannel(channelId!) : muteChannel(channelId!)}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg"
          >
            {isMuted ? (
              <BellOff className="w-5 h-5 text-slate-400" />
            ) : (
              <Bell className="w-5 h-5 text-slate-600 dark:text-slate-400" />
            )}
          </button>
          <button className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg">
            <Users className="w-5 h-5 text-slate-600 dark:text-slate-400" />
          </button>
          <button className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg">
            <MoreHorizontal className="w-5 h-5 text-slate-600 dark:text-slate-400" />
          </button>
        </div>
      </div>
      
      {/* Messages */}
      <div className="flex-1 overflow-y-auto custom-scrollbar px-4 py-4">
        {loadingMessages ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-16 h-16 rounded-2xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center mb-4">
              <Hash className="w-8 h-8 text-violet-600 dark:text-violet-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-2">
              Welcome to #{channel.name}
            </h3>
            <p className="text-sm text-slate-500 max-w-sm">
              This is the beginning of the channel. Start the conversation!
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {Array.from(groupedMessages.entries()).map(([date, msgs]) => (
              <div key={date}>
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
                  <span className="text-xs font-medium text-slate-500 px-2">{date}</span>
                  <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
                </div>
                <div className="space-y-4">
                  {msgs.map((msg) => (
                    <MessageBubble 
                      key={msg.id} 
                      message={msg} 
                      onReply={() => setReplyTo(msg)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      
      {/* Reply Preview */}
      {replyTo && (
        <div className="px-4 py-2 bg-slate-50 dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 flex items-center gap-3">
          <Reply className="w-4 h-4 text-violet-500" />
          <div className="flex-1 min-w-0">
            <p className="text-xs text-violet-600 font-medium">Replying to {replyTo.sender.name}</p>
            <p className="text-xs text-slate-500 truncate">{replyTo.content}</p>
          </div>
          <button onClick={() => setReplyTo(null)} className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded">
            <span className="text-xs text-slate-500">✕</span>
          </button>
        </div>
      )}
      
      {/* Input */}
      <form onSubmit={handleSend} className="px-4 py-3 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
        <div className="flex items-center gap-2">
          <button type="button" className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg">
            <Paperclip className="w-5 h-5 text-slate-400" />
          </button>
          <div className="flex-1 relative">
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={`Message #${channel.name}`}
              className="w-full px-4 py-2.5 bg-slate-100 dark:bg-slate-800 border-0 rounded-lg text-sm focus:ring-2 focus:ring-violet-500"
            />
          </div>
          <button type="button" className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg">
            <AtSign className="w-5 h-5 text-slate-400" />
          </button>
          <button type="button" className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg">
            <Smile className="w-5 h-5 text-slate-400" />
          </button>
          <button
            type="submit"
            disabled={!input.trim() || sendMutation.isLoading}
            className="p-2.5 bg-violet-600 hover:bg-violet-700 disabled:bg-slate-400 text-white rounded-lg transition-colors"
          >
            {sendMutation.isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

// Message Bubble Component
function MessageBubble({ message, onReply }: { message: Message, onReply: () => void }) {
  const [showActions, setShowActions] = useState(false)
  
  return (
    <div 
      className="group flex items-start gap-3"
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      <div className={cn(
        "w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-semibold bg-gradient-to-br flex-shrink-0",
        getAvatarColor(message.sender.name)
      )}>
        {getInitials(message.sender.name, message.sender.email)}
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="font-semibold text-sm text-slate-800 dark:text-white">
            {message.sender.name}
          </span>
          <span className="text-xs text-slate-400">
            {formatMessageTime(message.created_at)}
          </span>
          {message.is_edited && (
            <span className="text-[10px] text-slate-400">(edited)</span>
          )}
        </div>
        
        {message.reply_to && (
          <div className="flex items-center gap-2 mt-1 mb-2 pl-3 border-l-2 border-violet-300 dark:border-violet-700">
            <Reply className="w-3 h-3 text-violet-500" />
            <span className="text-xs text-slate-500">
              <span className="font-medium">{message.reply_to.sender_name}</span>: {message.reply_to.content}
            </span>
          </div>
        )}
        
        <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap break-words">
          {message.content}
        </p>
      </div>
      
      {/* Quick Actions */}
      <div className={cn(
        "flex items-center gap-1 transition-opacity",
        showActions ? "opacity-100" : "opacity-0"
      )}>
        <button 
          onClick={onReply}
          className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded"
        >
          <Reply className="w-4 h-4 text-slate-400" />
        </button>
      </div>
    </div>
  )
}
